import GQL from "@core/gql/gql";
import Html from "@core/libs/html";
import VoyoAppOptions from "../app/options";
import UserWithLogin from "./user_login";
import VoyoVideo from "./voyo_video";
import VideoPlaybackOptions from "@core/libs/video_playback_options";
import Profiles from "./profiles";
import Bookmarks from "./bookmarks";
import LocalStorage from "./local_storage";
import Events from "@core/libs/events";
import BookmarkGroupsModel, { Bookmark } from "@core/model/bookmarks";

class Voyo {
    constructor(
        protected html: Html,
        protected events: Events,
        protected user: UserWithLogin,
        protected voyoVideo: VoyoVideo,
        protected gql: GQL,
        protected profiles: Profiles,
        protected bookmarks: Bookmarks,
        protected localStorage: LocalStorage,
        protected options: VoyoAppOptions
    ) {
    }

    playTrailer(dest: string | HTMLElement, playbackOptions: VideoPlaybackOptions = {}): void {
        const trailer = this.html.q('.trailer', this.html.q(dest));

        if (!trailer) {
            return;
        }

        if (typeof playbackOptions.muted === 'undefined') {
            const isTrailerMuted = this.html.hasClass('body', 'trailer_muted');
            playbackOptions.muted = isTrailerMuted;
        }

        this.voyoVideo.initForTrailer(trailer, playbackOptions);
    }

    toggleBookmark(voyokey: string): void {
        const isBookmarked = this.bookmarks.isBookmarked('GRP_DEFAULT', voyokey);
    
        if (isBookmarked) {
            this.bookmarks.voyoBookmarkRemove('GRP_DEFAULT', voyokey);
            this.html.addClass('#content', 'myVoyo-removed-check');
    
            setTimeout(() => {
                this.html.removeClass('#content', 'myVoyo');
                this.html.removeClass('#content', 'myVoyo-removed-check');
            }, 2000);
        } else {
            this.bookmarks.voyoBookmarkAdd('GRP_DEFAULT', new Bookmark({entityId: voyokey}));
            this.html.addClass('#content', 'myVoyo myVoyo-check');
    
            // myVoyo-check is just for some animation, needs to be removed after 2s
            setTimeout(() => {
                this.html.removeClass('#content', 'myVoyo-check');
            }, 2000);
        }
    }

    /**
     * This function is used for toggling bookmarks on slides in sliders.
     * One content can be present in multiple sliders on the same page, so we need to toggle bookmark class on all of them.
     */
    toggleSlideBookmark(el: HTMLElement, voyokey: string): void {
        const isBookmarked = this.bookmarks.isBookmarked('GRP_DEFAULT', voyokey);
        let bookmarkPromise: Promise<any>;

        if (isBookmarked) {
            bookmarkPromise = this.bookmarks.voyoBookmarkRemove('GRP_DEFAULT', voyokey).then(() => {
                this.html.addClass(el.closest('.splide__slide') as HTMLElement, 'myVoyo-removed-check');

                // myVoyo-removed-check is just for the "removed" toast, needs to be removed after 2s
                setTimeout(() => {
                    const slides = this.html.qAll('.splide__slide[data-uniq="' + voyokey + '"]');
                    this.html.removeClass(slides, 'myVoyo');
                    this.html.removeClass(slides, 'myVoyo-removed-check');
                }, 2000);
            });
        } else {
            bookmarkPromise = this.bookmarks.voyoBookmarkAdd('GRP_DEFAULT', new Bookmark({entityId: voyokey})).then(() => {
                const slides = this.html.qAll('.splide__slide[data-uniq="' + voyokey + '"]');
                this.html.addClass(slides, 'myVoyo myVoyo-check');

                // myVoyo-check is just for some animation, needs to be removed after 2s
                setTimeout(() => {
                    this.html.removeClass(slides, 'myVoyo-check');
                }, 2000);
            });
        }

        bookmarkPromise.then(() => this.reloadMyVoyoBookmarkSlides());
    }

    // Used to add myVoyo class to cards on front page
    tagMyVoyoBookmarks(): void {
        const bookmarks = this.bookmarks.get()?.groupItems('GRP_DEFAULT');
        if (!bookmarks?.length) {
            return;
        }

        // Benchmarked different approaches and querying all items with uniq data and
        // then filtering out the bookmarked ones to get an array of htmlElements to
        // which we then add myVoyo class is the fastest and scales best with more bookmark items.
        const voyokeys = new Set(bookmarks.map(b => String(b.voyokey)));

        const items = this.html.qAll('[data-uniq]')
            .filter(el => voyokeys.has(this.html.getData(el, 'uniq')));

        this.html.addClass(items, 'myVoyo');
    }

    // Returns true if this page has a bookmarks or continue_watching box.
    // Sometimes editors remove bookmarks box and only continue_watching remains. This box also
    // uses bookmarks and we have to load them to fill the box.
    hasBookmarksBox(): boolean {
        return !!this.html.q('.voyobox.bookmarks') || !!this.html.q('.voyobox.continue_watching');
    }


    // Fetches the bookmarks box HTML, writes its slider fragments into the DOM, and
    // returns the bookmarks data embedded in it (if any).
    // This function is called from app.ts when the page is loaded and should not be called again
    // any cases where the bookmarks boxes are referenced should rely on the bookmarks-slides-fetched event.
    loadBookmarkSlides(): Promise<BookmarkGroupsModel | null> {
        if (!this.user.user?.token) {
            return Promise.resolve(null);
        }
        const urlPrefix = this.html.getData('body', 'urlPrefix');
        const url = urlPrefix + '/bookmark/voyobox_slides?' + this.user.cacheBuster;

        return this.html.fetchText(url, {timeout:4000,user:this.user.user})
            .then(html => {
                const [stayedatEl, myvoyoEl, bookmarksEl] = this.html.extractElements(html, [
                    '#stayedat',
                    '#myvoyo',
                    '#bookmarks-data',
                ]);

                const stayedat = stayedatEl?.innerHTML || '';
                const myvoyo = myvoyoEl?.innerHTML || '';

                if (stayedat.trim().length > 100) {
                    this.html.writeHTML('.voyobox.continue_watching .slider', stayedat);
                } else {
                    this.html.q('.voyobox.continue_watching')?.remove();
                }

                if (myvoyo.trim().length > 100) {
                    this.html.writeHTML('.voyobox.bookmarks .slider', myvoyo);
                } else {
                    this.html.q('.voyobox.bookmarks')?.remove();
                }

                this.events.sendEvent('bookmarks-slides-fetched', {});
                return this.parseBookmarksData(bookmarksEl?.dataset.bookmarks || '');
            })
            .catch(() => {
                this.html.q('.voyobox.continue_watching')?.remove();
                this.html.q('.voyobox.bookmarks')?.remove();

                return null;
            });
    }

    private parseBookmarksData(data: string): BookmarkGroupsModel | null {
        if (!data) {
            return null;
        }

        try {
            return new BookmarkGroupsModel(JSON.parse(data));
        } catch (error) {
            app.error('Error parsing bookmarks data:', error, data);
            return null;
        }
    }

    reloadMyVoyoBookmarkSlides(): Promise<any> {
        if (!this.user.user?.token) {
            return Promise.reject(false);
        }

        const urlPrefix = this.html.getData('body', 'urlPrefix');
        const url = urlPrefix + '/bookmark/voyobox_slides?' + this.user.cacheBuster;

        return this.html.fetchText(url, {timeout:4000,user:this.user.user})
            .then(html => {
                const myvoyo = this.html.extractHTML(html, '#myvoyo');

                if (myvoyo.trim().length > 100) {
                    this.html.writeHTML('.voyobox.bookmarks .slider', myvoyo);
                    this.events.sendEvent('myVoyo-slide-reloaded', {});
                } else {
                    this.html.q('.voyobox.bookmarks')?.remove();
                }
            })
            .catch(() => {
                this.html.q('.voyobox.bookmarks')?.remove();
            });
    }

    loadRecommendedSlides(): Promise<any> {
        if (!this.user.user?.token) {
            return Promise.resolve(false);
        }

        const url = '/recommended/voyobox_slides?' + this.user.cacheBuster;

        return this.html.fetchText(url, {timeout:4000,user:this.user.user})
            .then(html => {
                if (html.trim().length < 100) {
                    return false;
                }

                const [mainSlidesEl, mainSlidesTrackerEl, otherBoxesEl] = this.html.extractElements(html, [
                    '#main_slides',
                    '#main_slides_view_tracker',
                    '#other_boxes',
                ]);

                const mainSlides = mainSlidesEl?.innerHTML || '';
                const mainSlidesTracker = mainSlidesTrackerEl?.innerHTML || '';
                const otherBoxes = otherBoxesEl?.innerHTML || '';

                this.html.appendHTML('.recommended_main .slider', mainSlides);
                this.html.writeHTML('.recommended_main .view_tracker', mainSlidesTracker);

                if (otherBoxes.length > 100) {
                    const el = this.html.q('.recommended_main');
                    el?.insertAdjacentHTML('afterend', otherBoxes);
                }
            })
            .catch(err => {
                app.log('Error loading recommended slides', err);
                return false;
            });
    }

    loadSeasonEpisodes(uniq: string, season: string, template: string, dest: HTMLElement|string): Promise<any> {
        const currentSeason = this.html.getData(dest, 'current');
        const urlPrefix = this.html.getData('body', 'urlPrefix');
        const url = urlPrefix + '/info/' + uniq + '/seasons/' + encodeURIComponent(season) + '/episodes/' + template;

        if (season === currentSeason) {
            return Promise.resolve(false);
        }

        return this.html.fetchText(url)
            .then(html => {
                const episodesHTML = this.html.extractHTML(html, '#season_episodes');
                this.html.writeHTML(dest, episodesHTML);
                this.html.setData(dest, 'current', season);
            });
    }

    // Fetches the live streams box. Returns [needsUpdate, html] so the caller can
    // destroy the old Splide, inject the html and re-mount in the correct order.
    refreshStreams(): Promise<[boolean, string]> {
        return this.html.fetchText('/streams')
            .then(text => {
                const [track] = this.html.extractElements(text, ['#voyobox_streams .track']);

                const existingHash = this.html.getData('#voyobox_streams .track', 'hash');
                const newHash = track?.dataset.hash || '';

                if (existingHash === newHash) {
                    return [false, ''] as [boolean, string];
                }

                const html = track?.innerHTML || '';
                return [!!html, html] as [boolean, string];
            })
            .catch(err => {
                app.error('Error refreshing streams:', err);
                return [false, ''] as [boolean, string];
            });
    }

    // Shows details already baked into the page (in a <template>), for cases
    // where the data is already loaded and a fetch to /details would be redundant.
    showStaticContentDetails(templateId: string, placeholder = '.details_placeholder'): void {
        const template = this.html.q('#' + templateId);
        if (!template) {
            return;
        }

        this.voyoVideo.stopVideoPlayers();

        this.html.writeHTML(placeholder, template.innerHTML);
        this.html.removeClass(placeholder, 'hidden');
    }

    hideContentDetails(): void {
        this.html.writeHTML('.details_placeholder', '');
        this.html.addClass('.details_placeholder', 'hidden');
    }

    streamReminder(streamId: number): void {
        this.gql.liveStreamReminder(streamId)
            .then(() => {
                this.html.removeClass('#content .stream_reminder_toast', 'hidden');
                this.html.buttonDisable('#content .stream_reminder_button');

                setTimeout(() => {
                    this.html.addClass('#content .stream_reminder_toast', 'hidden');
                    this.html.buttonEnable('#content .stream_reminder_button');
                }, 2000);
            });
    }
}

export default Voyo;
